import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaFilter } from 'react-icons/fa';
import Select from 'react-select';
import styles from './PastIncidentsFilterMenu.module.css';
import {
  trackPastIncidentsDateFilterOpen,
  trackPastIncidentsDateFilterApply,
  trackPastIncidentsDateFilterReset,
  trackPastIncidentsDateFilterCancel,
  trackPastIncidentsPriorityFilterApply,
  trackPastIncidentsPriorityFilterReset,
  trackPastIncidentsBrandFilterApply,
  trackPastIncidentsBrandFilterReset,
  trackPastIncidentsExecSummaryDocumentFilterApply,
  trackPastIncidentsExecSummaryDocumentFilterReset,
  trackPastIncidentsFilterApply,
} from '../../../utils/modernUITracking';

const RELATIVE_OPTIONS = [
  { id: 'last7', label: 'Last 7 days', days: 7 },
  { id: 'last14', label: 'Last 14 days', days: 14 },
  { id: 'last30', label: 'Last 30 days', days: 30 },
  { id: 'last90', label: 'Last 90 days', days: 90 },
  { id: 'custom', label: 'Custom', days: null },
];

const PRIORITY_OPTIONS = [
  { id: 'EP1', label: 'EP1' },
  { id: 'EP2', label: 'EP2' },
  { id: 'EP3', label: 'EP3' },
  { id: 'EP4', label: 'EP4' },
];

/**
 * PastIncidentsFilterMenu
 * US1947404: Icon-only square filter button that opens a two-panel dropdown.
 *
 * Left panel: filter categories (Date).
 * Right panel: relative presets (Last 7/14/30/90 days) and Custom date pickers.
 *
 * Deferred apply: no fetch fires until the user clicks Apply.
 * Reset clears and applies null immediately.
 *
 * @param {Object} props
 * @param {Date|null} props.startDate    Currently applied start bound
 * @param {Date|null} props.endDate      Currently applied end bound
 * @param {string[]} props.priorities    Currently applied priority filter values (EP1–EP4)
 * @param {string[]} props.brands        Currently applied brand name filter values
 * @param {Array} props.brandOptions     Available brands ({ name }) for the multi-select
 * @param {boolean} props.execSummaryDocument Whether the Exec Summary Document Available filter is applied
 * @param {Error|null} props.brandsError Non-null when the brand list failed to load; shows error state in brand panel
 * @param {string|null} props.dateMode   Seeds the internal appliedMode on mount (e.g. 'custom' when restoring from URL params)
 * @param {(filter: { startDate: Date|null, endDate: Date|null, mode: string|null, priorities: string[], brands: string[], execSummaryDocument: boolean }) => void} props.onChange
 */
const PastIncidentsFilterMenu = ({
  startDate = null,
  endDate = null,
  priorities = [],
  brands = [],
  execSummaryDocument = false,
  brandOptions = [],
  brandsLoading = false,
  brandsError = null,
  dateMode = null,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });
  const [selectedCategory, setSelectedCategory] = useState('date');

  // appliedMode tracks what was last committed via Apply (drives button active state).
  const [appliedMode, setAppliedMode] = useState(dateMode ?? null);

  // pendingMode/Start/End are the in-modal uncommitted selections.
  const [pendingMode, setPendingMode] = useState(null);
  const [pendingStart, setPendingStart] = useState(null);
  const [pendingEnd, setPendingEnd] = useState(null);
  const [pendingPriorities, setPendingPriorities] = useState([]);
  const [pendingBrands, setPendingBrands] = useState([]);
  const [pendingExecSummaryDocument, setPendingExecSummaryDocument] = useState(false);

  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Memoize the react-select option/value objects so their identities are stable across
  // renders — rebuilding them inline on every render defeats react-select's internal
  // memoization and causes avoidable dropdown re-renders while the panel is open.
  const brandSelectOptions = useMemo(
    () => brandOptions.map(b => ({ value: b.name, label: b.name })),
    [brandOptions],
  );
  const brandSelectValue = useMemo(
    () => pendingBrands.map(b => ({ value: b, label: b })),
    [pendingBrands],
  );

  // When opening, initialise pending state from applied state.
  const handleOpen = useCallback(() => {
    setPendingMode(appliedMode);
    setPendingStart(appliedMode === 'custom' ? (startDate || null) : null);
    setPendingEnd(appliedMode === 'custom' ? (endDate || null) : null);
    setPendingPriorities(priorities || []);
    setPendingBrands(brands || []);
    setPendingExecSummaryDocument(execSummaryDocument);
    // Open on the most relevant category: brand if brands are active, priority if
    // priorities are active, the Document filter if only it is, otherwise default to date.
    const startCategory = brands.length > 0 ? 'brand' : priorities.length > 0 ? 'priority' : execSummaryDocument ? 'execSummaryDocument' : 'date';
    setSelectedCategory(startCategory);
    setOpen(true);
    trackPastIncidentsDateFilterOpen({ hasActiveFilter: appliedMode !== null || priorities.length > 0 || brands.length > 0 || execSummaryDocument, currentMode: appliedMode });
  }, [appliedMode, startDate, endDate, priorities, brands, execSummaryDocument]);

  // Position panel below and right-aligned to the button.
  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [open]);

  // Close on click outside (without applying).
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape (without applying).
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Compute the actual Date range from a mode and optional custom bounds.
  const computeRange = useCallback((mode, pStart, pEnd) => {
    if (!mode) return { start: null, end: null };
    if (mode === 'custom') return { start: pStart || null, end: pEnd || null };
    const opt = RELATIVE_OPTIONS.find((o) => o.id === mode);
    if (!opt || opt.days === null) return { start: null, end: null };
    const start = new Date(today);
    start.setDate(start.getDate() - opt.days);
    return { start, end: new Date(today) };
  }, [today]);

  const handleApply = useCallback(() => {
    const { start, end } = computeRange(pendingMode, pendingStart, pendingEnd);
    setAppliedMode(pendingMode);
    onChange({ startDate: start, endDate: end, mode: pendingMode, priorities: pendingPriorities, brands: pendingBrands, execSummaryDocument: pendingExecSummaryDocument });
    setOpen(false);
    trackPastIncidentsDateFilterApply({
      filterMode: pendingMode,
      hasStartDate: start !== null,
      hasEndDate: end !== null,
      rangeStartDate: start ? start.toISOString().split('T')[0] : null,
      rangeEndDate: end ? end.toISOString().split('T')[0] : null,
    });
    if (pendingPriorities.length > 0) {
      trackPastIncidentsPriorityFilterApply({ priorities: pendingPriorities });
    }
    if (pendingBrands.length > 0) {
      trackPastIncidentsBrandFilterApply({ brands: pendingBrands });
    }
    if (pendingExecSummaryDocument) {
      trackPastIncidentsExecSummaryDocumentFilterApply();
    }
    // Unified event — all active dimensions in one place for dashboard queries.
    const dimCount = (pendingMode !== null ? 1 : 0) + (pendingPriorities.length > 0 ? 1 : 0) + (pendingBrands.length > 0 ? 1 : 0) + (pendingExecSummaryDocument ? 1 : 0);
    trackPastIncidentsFilterApply({
      dimensionCount: dimCount,
      hasDate: pendingMode !== null,
      dateMode: pendingMode,
      priorityCount: pendingPriorities.length,
      priorities: pendingPriorities,
      brandCount: pendingBrands.length,
      brands: pendingBrands,
      hasExecSummaryDocument: pendingExecSummaryDocument,
    });
  }, [pendingMode, pendingStart, pendingEnd, pendingPriorities, pendingBrands, pendingExecSummaryDocument, computeRange, onChange]);

  // Cancel discards pending changes; the applied state is untouched.
  const handleCancel = useCallback(() => {
    setOpen(false);
    trackPastIncidentsDateFilterCancel({ hasActiveFilter: appliedMode !== null || priorities.length > 0 || brands.length > 0 || execSummaryDocument, currentMode: appliedMode });
  }, [appliedMode, priorities, brands, execSummaryDocument]);

  // Reset immediately clears the applied filter and closes.
  const handleReset = useCallback(() => {
    trackPastIncidentsDateFilterReset({ previousMode: appliedMode });
    if (priorities.length > 0) trackPastIncidentsPriorityFilterReset({ previousPriorities: priorities });
    if (brands.length > 0) trackPastIncidentsBrandFilterReset({ previousBrands: brands });
    if (execSummaryDocument) trackPastIncidentsExecSummaryDocumentFilterReset();
    setAppliedMode(null);
    setPendingMode(null);
    setPendingStart(null);
    setPendingEnd(null);
    setPendingPriorities([]);
    setPendingBrands([]);
    setPendingExecSummaryDocument(false);
    onChange({ startDate: null, endDate: null, mode: null, priorities: [], brands: [], execSummaryDocument: false });
    setOpen(false);
  }, [appliedMode, priorities, brands, execSummaryDocument, onChange]);

  const isActive = appliedMode !== null || priorities.length > 0 || brands.length > 0 || execSummaryDocument;
  const activeFilterCount = (appliedMode !== null ? 1 : 0) + priorities.length + brands.length + (execSummaryDocument ? 1 : 0);
  const hasInvertedRange = pendingMode === 'custom' && pendingStart && pendingEnd && pendingStart > pendingEnd;

  return (
    <div className={styles.menuContainer}>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.filterButton} ${isActive ? styles.filterButtonActive : ''}`}
        onClick={open ? handleCancel : handleOpen}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={isActive ? `Filter (${activeFilterCount} active)` : 'Filter'}
        data-testid="past-incidents-filter-button"
      >
        <FaFilter aria-hidden="true" />
      </button>
      {activeFilterCount > 0 && (
        <span className={styles.filterCountBadge} aria-hidden="true">
          {activeFilterCount}
        </span>
      )}

      {open && (
        <div
          ref={panelRef}
          className={styles.panel}
          role="dialog"
          aria-label="Filter past incidents"
          style={{ top: panelPos.top, right: panelPos.right }}
          data-testid="past-incidents-filter-dialog"
        >
          <div className={styles.panelBody}>
            {/* Left: category list */}
            <div className={styles.categoryList}>
              <button
                type="button"
                className={`${styles.categoryItem} ${selectedCategory === 'date' ? styles.categoryItemActive : ''}`}
                onClick={() => setSelectedCategory('date')}
              >
                Resolved by Date
                {pendingMode !== null && (
                  <span className={styles.categoryBadge}>1</span>
                )}
              </button>
              <button
                type="button"
                className={`${styles.categoryItem} ${selectedCategory === 'priority' ? styles.categoryItemActive : ''}`}
                onClick={() => setSelectedCategory('priority')}
                data-testid="filter-category-priority-button"
              >
                Priority
                {pendingPriorities.length > 0 && (
                  <span className={styles.categoryBadge}>{pendingPriorities.length}</span>
                )}
              </button>
              <button
                type="button"
                className={`${styles.categoryItem} ${selectedCategory === 'brand' ? styles.categoryItemActive : ''}`}
                onClick={() => setSelectedCategory('brand')}
                data-testid="filter-category-brand-button"
              >
                Brand
                {pendingBrands.length > 0 && (
                  <span className={styles.categoryBadge}>{pendingBrands.length}</span>
                )}
              </button>
              <button
                type="button"
                className={`${styles.categoryItem} ${selectedCategory === 'execSummaryDocument' ? styles.categoryItemActive : ''}`}
                onClick={() => setSelectedCategory('execSummaryDocument')}
                data-testid="filter-category-exec-summary-document-button"
              >
                Exec Summary Document
                {pendingExecSummaryDocument && (
                  <span className={styles.categoryBadge}>1</span>
                )}
              </button>
            </div>

            {/* Right: options for the selected category */}
            <div className={styles.optionsPanel}>
              {selectedCategory === 'date' && (
                <div className={styles.dateOptions}>
                  <button
                    type="button"
                    className={`${styles.optionItem} ${styles.anyOption} ${pendingMode === null ? styles.optionItemActive : ''}`}
                    onClick={() => { setPendingMode(null); setPendingStart(null); setPendingEnd(null); }}
                  >
                    <span className={`${styles.radioIndicator} ${pendingMode === null ? styles.radioIndicatorSelected : ''}`} aria-hidden="true" />
                    Any
                  </button>
                  {RELATIVE_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`${styles.optionItem} ${pendingMode === opt.id ? styles.optionItemActive : ''}`}
                      onClick={() => setPendingMode(opt.id)}
                      data-testid={`filter-date-range-${opt.id}`}
                    >
                      <span className={`${styles.radioIndicator} ${pendingMode === opt.id ? styles.radioIndicatorSelected : ''}`} aria-hidden="true" />
                      {opt.label}
                    </button>
                  ))}

                  {pendingMode === 'custom' && (
                    <div className={styles.customDates}>
                      <div className={styles.dateField}>
                        <span className={styles.fieldLabel}>From</span>
                        <div className={styles.pickerWrapper}>
                          <DatePicker
                            selected={pendingStart}
                            onChange={(d) => setPendingStart(d || null)}
                            selectsStart
                            startDate={pendingStart}
                            endDate={pendingEnd}
                            maxDate={pendingEnd || today}
                            placeholderText="YYYY-MM-DD"
                            dateFormat="yyyy-MM-dd"
                            popperProps={{ strategy: 'fixed' }}
                            wrapperClassName={styles.datePickerWrapper}
                            className={styles.dateInput}
                            aria-label="Start date"
                          />
                          {pendingStart && (
                            <button
                              type="button"
                              className={styles.clearIcon}
                              onClick={() => setPendingStart(null)}
                              aria-label="Clear start date"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>

                      <div className={styles.dateField}>
                        <span className={styles.fieldLabel}>To</span>
                        <div className={styles.pickerWrapper}>
                          <DatePicker
                            selected={pendingEnd}
                            onChange={(d) => setPendingEnd(d || null)}
                            selectsEnd
                            startDate={pendingStart}
                            endDate={pendingEnd}
                            minDate={pendingStart || undefined}
                            maxDate={today}
                            placeholderText="YYYY-MM-DD"
                            dateFormat="yyyy-MM-dd"
                            popperProps={{ strategy: 'fixed' }}
                            wrapperClassName={styles.datePickerWrapper}
                            className={styles.dateInput}
                            aria-label="End date"
                          />
                          {pendingEnd && (
                            <button
                              type="button"
                              className={styles.clearIcon}
                              onClick={() => setPendingEnd(null)}
                              aria-label="Clear end date"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>

                      {hasInvertedRange && (
                        <p className={styles.validationError} role="alert">
                          Start date must be on or before end date.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedCategory === 'priority' && (
                <div className={styles.priorityOptions}>
                  {PRIORITY_OPTIONS.map((opt) => {
                    const checked = pendingPriorities.includes(opt.id);
                    return (
                      <label key={opt.id} className={styles.priorityOption}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setPendingPriorities(prev =>
                              prev.includes(opt.id) ? prev.filter(p => p !== opt.id) : [...prev, opt.id]
                            );
                          }}
                          className={styles.priorityCheckbox}
                          data-testid={`filter-priority-checkbox-${opt.id.toLowerCase()}`}
                        />
                        <span
                          className={styles.priorityDot}
                          style={{ background: `var(--color-priority-${opt.id.toLowerCase()})` }}
                          aria-hidden="true"
                        />
                        <span className={styles.priorityLabel}>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {selectedCategory === 'execSummaryDocument' && (
                <div className={styles.priorityOptions}>
                  <label className={styles.priorityOption}>
                    <input
                      type="checkbox"
                      checked={pendingExecSummaryDocument}
                      onChange={() => setPendingExecSummaryDocument((prev) => !prev)}
                      className={styles.priorityCheckbox}
                      data-testid="filter-exec-summary-document-checkbox"
                    />
                    <span className={styles.priorityLabel}>Exec Summary Document Available</span>
                  </label>
                  <p className={styles.optionHelp}>Only incidents that have an Exec Summary Document link.</p>
                </div>
              )}

              {selectedCategory === 'brand' && (
                // onMouseDown stops the react-select clearIndicator from bubbling
                // to our document click-outside handler, which would close the panel.
                <div className={styles.brandOptions} onMouseDown={(e) => e.stopPropagation()}>
                  {/* Raw react-select below — doesn't forward data-testid, wrap externally.
                      classNamePrefix="brandSelect" here, NOT the usual "rs" — the
                      ".rs__control" idiom used elsewhere (ImpactForm.js, ProblemForm.js
                      SimpleMenu) won't match this one; use getByRole("combobox") instead. */}
                  {brandsError ? (
                    <p className={styles.brandsUnavailable}>Brands unavailable — try refreshing the page.</p>
                  ) : brandsLoading ? (
                    <p className={styles.brandsLoading}>Loading brands…</p>
                  ) : (
                  <div data-testid="filter-brand-select">
                    <Select
                      isMulti
                      options={brandSelectOptions}
                      value={brandSelectValue}
                      onChange={(selected) => setPendingBrands(selected ? selected.map(s => s.value) : [])}
                      placeholder="Select brands..."
                      classNamePrefix="brandSelect"
                      menuPosition="fixed"
                      styles={{
                        control: (base) => ({
                          ...base,
                          fontSize: '0.875rem',
                          minHeight: '36px',
                          borderColor: 'var(--border-subtle, #dee2e6)',
                          backgroundColor: 'var(--bg-surface, #f8f9fa)',
                        }),
                        menu: (base) => ({
                          ...base,
                          fontSize: '0.875rem',
                          zIndex: 400,
                          backgroundColor: 'var(--bg-elevated, #fff)',
                          color: 'var(--text-primary)',
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: 'var(--primary-100, #dbeafe)',
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: 'var(--primary-700, #1d4ed8)',
                          fontSize: '0.8rem',
                        }),
                      }}
                    />
                  </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.panelFooter}>
            <button
              type="button"
              className={styles.resetButton}
              onClick={handleReset}
            >
              Reset filters
            </button>
            <div className={styles.footerActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.applyButton}
                onClick={hasInvertedRange ? undefined : handleApply}
                disabled={hasInvertedRange}
                data-testid="filter-apply-button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PastIncidentsFilterMenu.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  priorities: PropTypes.arrayOf(PropTypes.string),
  brands: PropTypes.arrayOf(PropTypes.string),
  brandOptions: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  brandsLoading: PropTypes.bool,
  brandsError: PropTypes.object,
  execSummaryDocument: PropTypes.bool,
  dateMode: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default PastIncidentsFilterMenu;
